import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface GeminiOnboardingProps {
  /** Quando true, o tutorial fica aberto por padrão. */
  defaultOpen?: boolean;
}

/**
 * Tutorial passo-a-passo para pegar uma API key do Google Gemini (free tier).
 *
 * Usado dentro do ProviderForm quando o user seleciona Google. Pensado para
 * quem nunca usou nenhuma plataforma de IA via API.
 *
 * Limites do free tier mudam com frequência (Google já reduziu o RPD do
 * Flash em 2025), então a copy evita números absolutos.
 *   - Sem cartão de crédito
 *   - Conta Google é o suficiente
 */
export function GeminiOnboarding({ defaultOpen = false }: GeminiOnboardingProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="rounded-sm border border-[var(--accent-forest-border)] bg-[var(--bg-positive-tint)]"
      style={{ borderLeft: '3px solid var(--accent-forest)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
            recomendado · sem cartão · grátis
          </p>
          <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">
            Não tem API key ainda? Pegue uma do Google Gemini em ~2 minutos.
          </p>
          <p className="mt-1 font-sans text-xs text-[var(--ink-secondary)]">
            Centenas de análises por dia grátis. Funciona no modo URL/texto e no modo Tendências
            (web search nativo). Não pede cartão.
          </p>
        </div>
        <span
          aria-hidden
          className="ml-2 mt-1 font-mono text-xs text-[var(--accent-forest)]"
          style={{
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease-out',
          }}
        >
          ▸
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-[var(--accent-forest-border)] px-5 py-5">
              <ol className="space-y-5">
                <Step
                  index={1}
                  title="Abra o Google AI Studio"
                  body={
                    <>
                      Vá em{' '}
                      <a
                        href="https://aistudio.google.com/app/apikey"
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-[var(--accent-forest)] underline underline-offset-4"
                      >
                        aistudio.google.com/app/apikey
                      </a>{' '}
                      e faça login com sua conta Google. Não precisa de cartão.
                    </>
                  }
                />
                <Step
                  index={2}
                  title='Clique em "Create API key"'
                  body={
                    <>
                      Botão azul no topo da página. Se for sua primeira vez, ele abre um diálogo
                      pedindo pra criar (ou selecionar) um projeto Google Cloud.
                    </>
                  }
                />
                <Step
                  index={3}
                  title='Selecione "Create API key in new project"'
                  body={
                    <>
                      Se você não tem nenhum projeto Cloud, escolha essa opção. O AI Studio cria um
                      sozinho. Sem custos. Espere uns segundos.
                    </>
                  }
                />
                <Step
                  index={4}
                  title="Copie a chave"
                  body={
                    <>
                      Vai aparecer uma string começando com{' '}
                      <code className="rounded-sm bg-[var(--bg-paper-recessed)] px-1.5 py-0.5 font-mono text-[12px]">
                        AIza...
                      </code>
                      . Copie ela inteira. Você pode revisitar a chave no AI Studio depois — não
                      precisa guardar em nenhum gerenciador (mas não compartilhe).
                    </>
                  }
                />
                <Step
                  index={5}
                  title="Cole no campo abaixo"
                  body={
                    <>
                      Volte aqui, escolha provider <strong>Google</strong>, modelo{' '}
                      <strong>Gemini 2.5 Flash</strong>, e cole a chave no campo &ldquo;API
                      Key&rdquo;. Clique <strong>Validar e continuar</strong>. Sua chave vive apenas
                      em sessão — nunca vai pro nosso banco nem pra nossos logs.
                    </>
                  }
                />
              </ol>

              <div className="mt-6 grid grid-cols-1 gap-4 border-t border-[var(--accent-forest-border)] pt-5 sm:grid-cols-2">
                <Detail
                  label="o que é grátis"
                  body={
                    <ul className="space-y-1">
                      <li>→ Centenas de análises de perfil ou drafts por dia</li>
                      <li>→ Buscas com Grounding inclusas por dia (modo Tendências)</li>
                      <li>→ Limites exatos em ai.google.dev/pricing (mudam com frequência)</li>
                    </ul>
                  }
                />
                <Detail
                  label="o que cobra (caso passe)"
                  body={
                    <ul className="space-y-1">
                      <li>→ Análise simples: $0,01-$0,05 por chamada</li>
                      <li>→ Modo Tendências: ~$0,02-$0,04 (Grounding inclui $14/1k)</li>
                      <li>→ Cobra direto no seu Google Cloud, não na gente</li>
                    </ul>
                  }
                />
              </div>

              <div className="mt-5 rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-paper)] px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                  segurança
                </p>
                <p className="mt-1 font-sans text-xs text-[var(--ink-secondary)]">
                  Sua chave fica apenas em memória de sessão (nunca em banco, nunca em log). Quando
                  você sair ou a sessão expirar, a chave é descartada. Nas configurações do AI
                  Studio você pode adicionar restrições à chave (ex: só certos IPs ou domínios) —
                  recomendado quando passar do uso de teste.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Step({ index, title, body }: { index: number; title: string; body: React.ReactNode }) {
  return (
    <li className="flex items-start gap-4">
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-[var(--accent-forest-border)] bg-[var(--accent-forest-bg)] font-mono text-xs font-semibold tabular-nums"
        style={{ color: 'var(--accent-forest)' }}
      >
        {index.toString().padStart(2, '0')}
      </span>
      <div>
        <p className="font-serif text-[15px] font-medium text-[var(--ink-primary)]">{title}</p>
        <p className="mt-1 font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">{body}</p>
      </div>
    </li>
  );
}

function Detail({ label, body }: { label: string; body: React.ReactNode }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
        {label}
      </p>
      <div className="mt-2 font-sans text-xs leading-relaxed text-[var(--ink-secondary)]">
        {body}
      </div>
    </div>
  );
}
